import { Controller } from "@hotwired/stimulus"

// Desktop dropdown for an <input type="time">, modeled on Chrome's native time picker: hour,
// minute, and AM/PM columns that write the input's HH:MM value as they are clicked or arrowed
// through. It renders into a shadow root carrying the same easepick stylesheets the datepicker
// controller loads, so the two pickers look alike. Mobile keeps the native picker, as the
// datepicker does.
export default class extends Controller {
  connect = () => {
    const isMobileDevice = window.matchMedia("only screen and (max-width: 760px)").matches
    if (isMobileDevice) return

    this.element.parentElement.classList.add("relative")
    this.element.insertAdjacentHTML(
      "afterend",
      `<div class='absolute bottom-0 my-auto pointer-events-none right-3 size-4 text-gray-600 top-0'>${`<svg id="clock-o" class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>`}</div>`
    )
    this.icon = this.element.nextElementSibling

    const stylesheets = [
      "https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css",
      "/datepicker.css",
    ]

    const wrapper = document.createElement("span")
    wrapper.className = "easepick-wrapper"
    Object.assign(wrapper.style, {
      display: "none",
      pointerEvents: "none",
      position: "absolute",
    })
    const shadowRoot = wrapper.attachShadow({ mode: "open" })

    let pendingStylesheets = stylesheets.length
    stylesheets.forEach((href) => {
      const link = document.createElement("link")
      const loaded = () => {
        pendingStylesheets -= 1
        if (pendingStylesheets === 0) wrapper.style.display = ""
      }
      link.addEventListener("error", loaded)
      link.addEventListener("load", loaded)
      link.href = href
      link.rel = "stylesheet"
      shadowRoot.append(link)
    })

    const style = document.createElement("style")
    style.textContent = `
      .container {
        transition: none;
      }
      .reset-button {
        align-items: center;
        align-self: end;
        background-color: transparent;
        border: 1px solid transparent;
        border-radius: 2px;
        color: var(--color-btn-secondary-fg);
        cursor: pointer;
        display: flex;
        grid-column: 3;
        grid-row: 1;
        justify-content: center;
        justify-self: end;
        padding: 4px 7px;
      }
      .reset-button:hover {
        background-color: var(--color-bg-secondary);
      }
      .reset-button > svg {
        color: var(--color-btn-secondary-fg);
        pointer-events: none;
      }
      .reset-button:hover > svg {
        color: var(--color-fg-primary);
      }
      .time-cell {
        border: 1px solid transparent;
        border-radius: var(--border-radius);
        box-sizing: border-box;
        cursor: pointer;
        height: var(--day-height);
        line-height: calc(var(--day-height) - 2px);
        text-align: center;
      }
      .time-cell:hover {
        border-color: var(--color-fg-primary);
        color: var(--color-fg-primary);
      }
      .time-cell.selected {
        background-color: var(--color-fg-primary);
        color: var(--color-fg-selected);
      }
      .time-column {
        border-radius: var(--border-radius);
        height: calc(var(--day-height) * 6);
        overflow-y: auto;
        position: relative;
        scrollbar-width: none;
      }
      .time-column::-webkit-scrollbar {
        display: none;
      }
      .time-column[data-part="period"] {
        align-self: start;
        grid-column: 3;
        grid-row: 1;
        height: auto;
      }
      .time-column.active {
        background-color: var(--color-bg-secondary);
      }
      .time-grid {
        column-gap: 4px;
        display: grid;
        grid-template-columns: repeat(3, var(--day-width));
        padding: 10px;
      }
    `
    shadowRoot.append(style)

    const container = document.createElement("div")
    container.className = "container"
    container.innerHTML = `
      <main>
        <div class="calendars grid-1">
          <div class="calendar">
            <div class="time-grid">
              ${[
                { part: "hour", values: Array.from({ length: 12 }, (_, i) => i || 12) },
                { part: "minute", values: Array.from({ length: 60 }, (_, i) => i) },
                { part: "period", values: ["AM", "PM"] },
              ]
                .map(
                  ({ part, values }) => `
                  <div class="time-column" data-part="${part}">
                    ${values
                      .map(
                        (value) =>
                          `<div class="time-cell unit" data-value="${value}">${part === "period" ? value : String(value).padStart(2, "0")}</div>`
                      )
                      .join("")}
                  </div>
                `
                )
                .join("")}
              <button aria-label="Remove Time" class="reset-button unit" type="button">${`<svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`}</button>
            </div>
          </div>
        </div>
      </main>
    `
    shadowRoot.append(container)

    // Keeps focus on the input, which owns the keyboard handling, while the panel is clicked.
    container.addEventListener("mousedown", (e) => e.preventDefault())
    container.addEventListener("click", (e) => {
      if (e.target.closest(".reset-button")) {
        this.change("")
        this.hide()
        return
      }

      const cell = e.target.closest(".time-cell")
      if (!cell) return

      const part = cell.closest(".time-column").dataset.part
      this.#select(part, part === "period" ? cell.dataset.value : Number(cell.dataset.value))
    })

    this.element.parentElement.append(wrapper)

    this.picker = { ui: { container, shadowRoot, wrapper } }
    // Read-only, like the datepicker's easepick input, so Chrome's own time picker button and
    // segment editing stay out of the way. Browsers skip read-only fields when checking
    // `required`, so a form that requires a time must enforce it server-side.
    this.element.readOnly = true
    this.element.addEventListener("click", this.show)
    this.element.addEventListener("keydown", this.keydown)
  }

  disconnect = () => {
    if (!this.picker) return

    this.hide()
    this.element.readOnly = false
    this.element.removeEventListener("click", this.show)
    this.element.removeEventListener("keydown", this.keydown)
    this.icon.remove()
    this.picker.ui.wrapper.remove()
  }

  change = (value) => {
    if (this.element.value === value) return

    this.element.value = value
    this.element.dispatchEvent(new Event("input", { bubbles: true }))
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
  }

  documentClick = (e) => {
    if (e.target === this.element || e.composedPath().includes(this.picker.ui.wrapper)) return

    this.hide()
  }

  hide = () => {
    if (!this.isOpen) return

    this.isOpen = false
    this.picker.ui.container.classList.remove("show")
    window.removeEventListener("resize", this.reposition)
    window.removeEventListener("scroll", this.reposition, true)
    document.removeEventListener("click", this.documentClick, true)
  }

  keydown = (e) => {
    if (!this.isOpen) {
      if ([" ", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault()
        this.show()
      }
      return
    }

    // AM/PM is a pair of toggles rather than a column, so arrowing sideways only moves between
    // hours and minutes; A and P set it instead, as they do in Chrome's time field.
    const scrollingParts = ["hour", "minute"]
    const activeIndex = scrollingParts.indexOf(this.activePart)

    switch (e.key) {
      case "A":
      case "P":
      case "a":
      case "p":
        e.preventDefault()
        this.#select("period", e.key.toUpperCase() === "A" ? "AM" : "PM")
        break
      case "ArrowDown":
      case "ArrowUp": {
        e.preventDefault()
        const values = [
          ...this.picker.ui.container.querySelectorAll(
            `.time-column[data-part="${this.activePart}"] .time-cell`
          ),
        ].map((cell) => Number(cell.dataset.value))
        const current = (this.#parseTime(this.element.value) || {
          hour: 12,
          minute: 0,
          period: "PM",
        })[this.activePart]
        const index = values.indexOf(current)
        const delta = e.key === "ArrowDown" ? 1 : -1

        this.#select(this.activePart, values[(index + delta + values.length) % values.length])
        break
      }
      case "ArrowLeft":
      case "ArrowRight":
        e.preventDefault()
        this.activePart =
          scrollingParts[
            (activeIndex + (e.key === "ArrowRight" ? 1 : -1) + scrollingParts.length) %
              scrollingParts.length
          ]
        this.#render()
        break
      case "Backspace":
      case "Delete":
        e.preventDefault()
        this.change("")
        this.#render()
        break
      case "Enter":
        e.preventDefault()
        this.hide()
        break
      case "Escape":
        e.preventDefault()
        this.change(this.originalValue)
        this.hide()
        break
      case "Tab":
        this.hide()
        break
    }
  }

  reposition = () => {
    const container = this.picker.ui.container
    const rect = this.element.getBoundingClientRect()

    let top = rect.bottom
    if (
      window.innerHeight < top + container.offsetHeight &&
      rect.top - container.offsetHeight >= 0
    ) {
      top = rect.top - container.offsetHeight
    }

    Object.assign(container.style, {
      left: `${rect.left}px`,
      position: "fixed",
      top: `${top}px`,
    })
  }

  show = () => {
    if (this.element.matches(":disabled") || this.isOpen) return

    this.isOpen = true
    this.originalValue = this.element.value
    this.activePart = "hour"
    this.#render()
    this.picker.ui.container.classList.add("show")
    this.reposition()
    window.addEventListener("scroll", this.reposition, true)
    window.addEventListener("resize", this.reposition)
    this.#scrollToSelected({ alignTop: true })
    document.addEventListener("click", this.documentClick, true)
  }

  #parseTime = (value) => {
    if (!value) return null

    const [hours, minutes] = value.split(":").map(Number)
    return { hour: hours % 12 || 12, minute: minutes, period: hours >= 12 ? "PM" : "AM" }
  }

  #render = () => {
    const parts = this.#parseTime(this.element.value)
    this.picker.ui.container.querySelectorAll(".time-column").forEach((column) => {
      const part = column.dataset.part
      column.classList.toggle("active", part === this.activePart)
      column.querySelectorAll(".time-cell").forEach((cell) => {
        cell.classList.toggle("selected", !!parts && String(parts[part]) === cell.dataset.value)
      })
    })
  }

  #scrollToSelected = ({ alignTop }) => {
    this.picker.ui.container.querySelectorAll(".time-column").forEach((column) => {
      const cell = column.querySelector(".time-cell.selected")
      if (!cell) {
        if (alignTop) column.scrollTop = 0
        return
      }

      if (alignTop || cell.offsetTop < column.scrollTop) {
        column.scrollTop = cell.offsetTop
      } else if (cell.offsetTop + cell.offsetHeight > column.scrollTop + column.clientHeight) {
        column.scrollTop = cell.offsetTop + cell.offsetHeight - column.clientHeight
      }
    })
  }

  // Sets one part and writes the whole value at once, as Chrome does: picking any part of an
  // empty time fills the other parts with 12:00 PM defaults.
  #select = (part, value) => {
    const parts = {
      ...(this.#parseTime(this.element.value) || { hour: 12, minute: 0, period: "PM" }),
      [part]: value,
    }
    const hours = String((parts.hour % 12) + (parts.period === "PM" ? 12 : 0)).padStart(2, "0")

    if (part !== "period") this.activePart = part
    this.change(`${hours}:${String(parts.minute).padStart(2, "0")}`)
    this.#render()
    this.#scrollToSelected({ alignTop: false })
  }
}
